import React from 'react';
import { bool } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { LINE_ITEM_SALE_TAX, LINE_ITEM_CUSTOMER_COMMISSION_VAT, propTypes } from '../../util/types';
import { vatFromTransaction, rateLabel } from '../../util/vat';

import css from './BookingBreakdown.css';

const { Money } = sdkTypes;

// The customer's VAT line of a given code, if the customer pays it.
const customerLine = (transaction, code) =>
  transaction.attributes.lineItems.find(
    li =>
      li.code === code &&
      !li.reversal &&
      (!li.includeFor || li.includeFor.includes('customer')) &&
      li.lineTotal.amount > 0
  );

// The customer's VAT, after the work and the service fee: one row when both
// carry the same rate label, the work's and the fee's apart otherwise -- the
// rule the offer confirm step's price box follows (confirm step spec §3).
const LineItemCustomerVatMaybe = props => {
  const { transaction, isCustomer, intl } = props;
  if (!isCustomer) return null;
  const workVat = customerLine(transaction, LINE_ITEM_SALE_TAX);
  const feeVat = customerLine(transaction, LINE_ITEM_CUSTOMER_COMMISSION_VAT);
  if (!workVat && !feeVat) return null;

  const { customer, serviceFee } = vatFromTransaction(transaction);
  const labelFor = (line, supply) =>
    rateLabel(
      { rate: Number(line.percentage) / 100, label: (supply && supply.label) || 'VAT' },
      intl.locale
    );
  const workLabel = workVat ? labelFor(workVat, customer) : null;
  const feeLabel = feeVat ? labelFor(feeVat, serviceFee) : null;

  const rows =
    workVat && feeVat && workLabel === feeLabel
      ? [
          {
            key: 'vat',
            messageId: 'BookingBreakdown.vatLine',
            rateLabel: workLabel,
            amount: new Money(
              workVat.lineTotal.amount + feeVat.lineTotal.amount,
              workVat.lineTotal.currency
            ),
          },
        ]
      : [
          workVat && {
            key: 'vat',
            messageId: 'BookingBreakdown.vatLine',
            rateLabel: workLabel,
            amount: workVat.lineTotal,
          },
          feeVat && {
            key: 'feeVat',
            messageId: 'BookingBreakdown.vatOnFee',
            rateLabel: feeLabel,
            amount: feeVat.lineTotal,
          },
        ].filter(Boolean);

  return (
    <>
      {rows.map(row => (
        <div key={row.key} className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id={row.messageId} values={{ rateLabel: row.rateLabel }} />
          </span>
          <span className={css.itemValue}>{formatMoney(intl, row.amount)}</span>
        </div>
      ))}
    </>
  );
};

LineItemCustomerVatMaybe.propTypes = {
  transaction: propTypes.transaction.isRequired,
  isCustomer: bool.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemCustomerVatMaybe;
